import { Upload, type DetailedError } from "tus-js-client";
import { VIDEO_STORAGE_BUCKET } from "../../../shared/partenaireVideos";

export const VIDEO_TUS_CHUNK_BYTES = 6_291_456;
export const VIDEO_UPLOAD_EXPIRED = "L'envoi a expiré — relancez l'envoi de la vidéo.";
export const VIDEO_TUS_RETRY_DELAYS = [0, 1000, 3000, 5000, 10000, 20000, 30000, 60000];

type SignedVideo = {
  path: string;
  mimeType: string;
  uploadToken: string;
  tusEndpoint: string;
  expiresAt: number;
};

// Aucune URL, empreinte ou signature de reprise n'est persistée.
const noResumeStorage = {
  findAllUploads: async () => [],
  findUploadsByFingerprint: async () => [],
  removeUpload: async () => {},
  addUpload: async () => "",
};

export function isExpiredVideoUpload(expiresAt: number, status = 0, body = "") {
  return Date.now() >= expiresAt || status === 401 || status === 403 ||
    /(?:token|jwt|signature).*(?:expired|expiration)|(?:expired).*(?:token|jwt|signature)/i.test(body);
}

/** Reprise uniquement en mémoire : même Upload, même chemin, jamais de nouvelle signature. */
export async function uploadVideoWithTus(file: File, signed: SignedVideo, onProgress: (percent: number) => void) {
  if (!signed.uploadToken || !signed.tusEndpoint || !Number.isFinite(signed.expiresAt)) {
    throw new Error("Signature d'envoi vidéo incomplète — relancez l'envoi.");
  }
  if (isExpiredVideoUpload(signed.expiresAt)) throw new Error(VIDEO_UPLOAD_EXPIRED);

  await new Promise<void>((resolve, reject) => {
    let settled = false;
    let paused = !navigator.onLine;
    let pausePromise = Promise.resolve();
    let expiryTimer: ReturnType<typeof setTimeout>;

    const leave = (event: BeforeUnloadEvent) => {
      console.warn("[partenaire-videos] départ pendant l'envoi, objet partiel à examiner", signed.path);
      event.preventDefault();
      event.returnValue = "";
    };
    const cleanup = () => {
      clearTimeout(expiryTimer);
      window.removeEventListener("beforeunload", leave);
      window.removeEventListener("offline", offline);
      window.removeEventListener("online", online);
    };
    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      // Ne pas supprimer l'objet : journalisation et purge différée au backlog.
      void upload.abort(false).catch(() => {});
      reject(new Error(message));
    };
    const upload = new Upload(file, {
      endpoint: signed.tusEndpoint,
      headers: { "x-signature": signed.uploadToken },
      metadata: {
        bucketName: VIDEO_STORAGE_BUCKET,
        objectName: signed.path,
        contentType: signed.mimeType,
        cacheControl: "3600",
      },
      chunkSize: VIDEO_TUS_CHUNK_BYTES,
      uploadDataDuringCreation: true,
      storeFingerprintForResuming: false,
      urlStorage: noResumeStorage,
      retryDelays: VIDEO_TUS_RETRY_DELAYS,
      onBeforeRequest: () => {
        if (isExpiredVideoUpload(signed.expiresAt)) throw new Error(VIDEO_UPLOAD_EXPIRED);
      },
      onShouldRetry: (error: DetailedError) => {
        const status = error.originalResponse?.getStatus() || 0;
        if (isExpiredVideoUpload(signed.expiresAt, status, error.originalResponse?.getBody())) return false;
        return status === 0 || status === 408 || status === 409 || status === 423 || status === 429 || status >= 500;
      },
      onProgress: (sent, total) => {
        if (!settled && total > 0) onProgress(Math.min(99, Math.round(sent * 100 / total)));
      },
      onSuccess: () => {
        if (settled) return;
        if (isExpiredVideoUpload(signed.expiresAt)) { fail(VIDEO_UPLOAD_EXPIRED); return; }
        settled = true;
        cleanup();
        onProgress(100);
        resolve();
      },
      onError: error => {
        const response = (error as DetailedError).originalResponse;
        const status = response?.getStatus() || 0;
        if (isExpiredVideoUpload(signed.expiresAt, status, response?.getBody())) {
          fail(VIDEO_UPLOAD_EXPIRED);
        } else if (paused && !navigator.onLine) {
          // Le retour en ligne redémarre cette instance à son offset confirmé.
        } else {
          fail(status ? `Échec de l'envoi vidéo (Storage ${status}) — relancez l'envoi.` :
            "Erreur réseau pendant l'envoi vidéo — relancez l'envoi.");
        }
      },
    });
    function offline() {
      if (settled) return;
      paused = true;
      pausePromise = upload.abort(false).catch(() => {});
    }
    function online() {
      if (settled || !paused) return;
      paused = false;
      void pausePromise.then(() => {
        if (settled || !navigator.onLine) return;
        if (isExpiredVideoUpload(signed.expiresAt)) fail(VIDEO_UPLOAD_EXPIRED);
        else upload.start();
      });
    }
    window.addEventListener("beforeunload", leave);
    window.addEventListener("offline", offline);
    window.addEventListener("online", online);
    expiryTimer = setTimeout(() => fail(VIDEO_UPLOAD_EXPIRED), signed.expiresAt - Date.now());
    if (!paused) upload.start();
  });
}
